// frontend/src/lib/ruleSets.js
// Frontend mirror of backend/app/face_definitions.py rule-set accessors.
// SSOT: shared/face-definitions.json `ruleSets` (Format editor rework 2026-09-09).
import faceDefinitions from "../../../shared/face-definitions.json";

const { faceTypes, faces, ruleSets } = faceDefinitions;

export const RULE_SET_ORDER = [...(ruleSets?._order ?? [])];

// Rule sets whose Format is shot on a course rather than an ends-based
// distance schedule (owner feedback round 2, #2397 follow-up 2026-09-09):
// these are offered ONLY in the Format editor's Course mode. Order preserved
// from RULE_SET_ORDER.
export const ENDS_RULE_SETS = RULE_SET_ORDER.filter((fam) => !ruleSetIsCourseOnly(fam));

// The mirror image: the rule sets the Format editor's COURSE mode offers —
// WA field · NFAA field · NFAA hunter · 3D. Course mode uses the SAME
// `RuleSetCard` radiogroup and the SAME Faces section as ends mode; only this
// list, the wording and the row defaults differ (owner feedback 2026-09-10).
export const COURSE_RULE_SETS = RULE_SET_ORDER.filter((fam) => ruleSetIsCourseOnly(fam));

export function ruleSetIsCourseOnly(family) {
  return !!ruleSets?.[family]?.course_only;
}

export function ruleSetLabel(family) {
  return ruleSets?.[family]?.label ?? family;
}

export function facesForRuleSet(family) {
  const types = new Set(ruleSets?.[family]?.face_types ?? []);
  return Object.keys(faces).filter((code) => types.has(faces[code].face_type));
}

export function ruleSetForFace(face) {
  const ft = faces[face]?.face_type;
  if (!ft) return null;
  return RULE_SET_ORDER.find((fam) => (ruleSets[fam]?.face_types ?? []).includes(ft)) ?? null;
}

// Mirror of backend `face_definitions.face_is_course`: is this FACE code shot
// on a walk-around course (Field / Hunter / 3D / WA field)? Resolves the face
// to its rule set and reads `course_only`; a face outside every rule set
// answers false (fail-open, like `ruleSetForFace`). This is how a ROUND —
// which carries `target_face` but no `structure_type` — is recognised as a
// course (sandbox QA 2026-09-18, round 488).
export function faceIsCourse(face) {
  return ruleSetIsCourseOnly(ruleSetForFace(face) ?? "");
}

export function ruleSetHasX(family) {
  return (ruleSets?.[family]?.face_types ?? []).some((ft) =>
    (faceTypes[ft]?.legal_scores ?? []).includes("X"));
}

export function defaultFaceForRuleSet(family) {
  return facesForRuleSet(family)[0] ?? "wa40";
}

// Rule-set card thumbnail (Format editor rework 2026-09-09) — the faces.<code>
// row whose TargetFace glyph represents the family on its `.fe-ruleset-card`.
export function ruleSetIconFace(family) {
  return ruleSets?.[family]?.icon_face ?? defaultFaceForRuleSet(family);
}

// Terse "Scoring: …" / "Used: …" meta lines printed on that same card
// (owner feedback round 2, #2397 follow-up 2026-09-09 — replaces the old
// free-text `description`, which was inconsistent in length and phrasing).
export function ruleSetScoring(family) {
  return ruleSets?.[family]?.scoring ?? "";
}

export function ruleSetUsed(family) {
  return ruleSets?.[family]?.used ?? "";
}
